import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, RefreshCw, Save, X, Power, PowerOff, Tag, Calendar, Percent, DollarSign } from 'lucide-react';
import { Coupon, CreateCouponRequest } from '../interfaces/Coupon';
import { couponService } from '../services/couponService';
import { DefaultLoader } from '../components';

const Coupons: React.FC = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);
  const [formData, setFormData] = useState<CreateCouponRequest>({
    code: '',
    description: '',
    type: 'percentage',
    value: 0,
    usageLimit: undefined,
    validFrom: '',
    validUntil: '',
    minimumAmount: undefined,
  });

  useEffect(() => {
    loadCoupons();
  }, []);

  const loadCoupons = async () => {
    try {
      setLoading(true);
      const data = await couponService.getAll();
      setCoupons(data);
    } catch (error) {
      console.error('Erro ao carregar cupons:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingCoupon) {
        await couponService.update(editingCoupon.id, formData);
      } else {
        await couponService.create(formData);
      }
      
      handleCancelForm();
      loadCoupons();
    } catch (error) {
      console.error('Erro ao salvar cupom:', error);
    }
  };

  const handleEdit = (coupon: Coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      description: coupon.description || '',
      type: coupon.type,
      value: coupon.value,
      usageLimit: coupon.usageLimit,
      validFrom: coupon.validFrom ? coupon.validFrom.split('T')[0] : '',
      validUntil: coupon.validUntil ? coupon.validUntil.split('T')[0] : '',
      minimumAmount: coupon.minimumAmount,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este cupom?')) {
      return;
    }
    
    try {
      await couponService.delete(id);
      loadCoupons();
    } catch (error) {
      console.error('Erro ao excluir cupom:', error);
    }
  };

  const handleDeactivate = async (id: string) => {
    try {
      await couponService.deactivate(id);
      loadCoupons();
    } catch (error) {
      console.error('Erro ao desativar cupom:', error);
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingCoupon(null);
    setFormData({
      code: '',
      description: '',
      type: 'percentage',
      value: 0,
      usageLimit: undefined,
      validFrom: '',
      validUntil: '',
      minimumAmount: undefined,
    });
  };

  const formatValue = (coupon: Coupon) => {
    const value = Number(coupon.value);
    return coupon.type === 'percentage' 
      ? `${value}%` 
      : `R$ ${value.toFixed(2).replace('.', ',')}`;
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      active: 'badge-completed',
      inactive: 'badge-cancelled',
      expired: 'badge-failed',
    };
    return badges[status as keyof typeof badges] || 'badge-pending';
  };

  const getStatusText = (status: string) => {
    const texts = {
      active: 'Ativo',
      inactive: 'Inativo',
      expired: 'Expirado',
    };
    return texts[status as keyof typeof texts] || status;
  };

  if (loading) {
    return <DefaultLoader title="Cupons" messages={['Carregando cupons...', 'Processando descontos...', 'Organizando dados...']} />;
  }

  return (
    <div>
      <div className="d-flex justify-between align-center mb-3">
        <div>
          <h2 className="mb-1">Cupons de Desconto</h2>
          <p className="mb-0" style={{ color: 'var(--color-dimgray)' }}>
            Gerencie cupons de desconto para o sistema
          </p>
        </div>
        <div className="d-flex gap-2">
          <button onClick={loadCoupons} className="btn btn-secondary">
            <RefreshCw size={16} />
            Atualizar
          </button>
          <button onClick={() => setShowForm(true)} className="btn btn-primary">
            <Plus size={16} />
            Novo Cupom
          </button>
        </div>
      </div>

      {showForm && (
        <div className="card mb-3">
          <h3 className="mb-3">
            {editingCoupon ? 'Editar Cupom' : 'Novo Cupom'}
          </h3>
          
          <form onSubmit={handleSubmit}>
            <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label className="form-label">Código *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  required
                  placeholder="Ex: DESCONTO10"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Tipo *</label>
                <select
                  className="form-select"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as 'percentage' | 'fixed' })}
                  required
                >
                  <option value="percentage">Percentual</option>
                  <option value="fixed">Valor Fixo</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">
                  Valor * {formData.type === 'percentage' ? '(%)' : '(R$)'}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="form-input"
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: parseFloat(e.target.value) || 0 })}
                  required
                  placeholder={formData.type === 'percentage' ? '10' : '50.00'}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Limite de Uso</label>
                <input
                  type="number"
                  min="1"
                  className="form-input"
                  value={formData.usageLimit || ''}
                  onChange={(e) => setFormData({ ...formData, usageLimit: e.target.value ? parseInt(e.target.value) : undefined })}
                  placeholder="Deixe vazio para ilimitado"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Válido A Partir De</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.validFrom}
                  onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Válido Até</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.validUntil}
                  onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Valor Mínimo (R$)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="form-input"
                  value={formData.minimumAmount || ''}
                  onChange={(e) => setFormData({ ...formData, minimumAmount: e.target.value ? parseFloat(e.target.value) : undefined })}
                  placeholder="Ex: 100.00"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Descrição</label>
              <input
                type="text"
                className="form-input"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrição do cupom"
              />
            </div>

            <div className="d-flex gap-2 justify-between">
              <button type="button" onClick={handleCancelForm} className="btn btn-secondary">
                <X size={16} />
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary">
                <Save size={16} />
                {editingCoupon ? 'Atualizar' : 'Criar'} Cupom
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Código</th>
              <th>Tipo</th>
              <th>Valor</th>
              <th>Uso</th>
              <th>Validade</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {coupons.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center">
                  Nenhum cupom encontrado
                </td>
              </tr>
            ) : (
              coupons.map((coupon) => (
                <tr key={coupon.id}>
                  <td>
                    <div className="d-flex align-center gap-1">
                      <Tag size={16} style={{ color: 'var(--color-dodgerblue)' }} />
                      <strong>{coupon.code}</strong>
                    </div>
                    {coupon.description && (
                      <div style={{ fontSize: '12px', color: 'var(--color-dimgray)' }}>
                        {coupon.description}
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="d-flex align-center gap-1">
                      {coupon.type === 'percentage' ? (
                        <Percent size={14} />
                      ) : (
                        <DollarSign size={14} />
                      )}
                      {coupon.type === 'percentage' ? 'Percentual' : 'Valor Fixo'}
                    </div>
                  </td>
                  <td>
                    <strong>{formatValue(coupon)}</strong>
                    {coupon.minimumAmount && (
                      <div style={{ fontSize: '12px', color: 'var(--color-dimgray)' }}>
                        Min: R$ {Number(coupon.minimumAmount).toFixed(2).replace('.', ',')}
                      </div>
                    )}
                  </td>
                  <td>
                    {coupon.usageCount}
                    {coupon.usageLimit && ` / ${coupon.usageLimit}`}
                  </td>
                  <td>
                    {coupon.validFrom || coupon.validUntil ? (
                      <div className="d-flex align-center gap-1">
                        <Calendar size={14} />
                        <div style={{ fontSize: '12px' }}>
                          {coupon.validFrom && (
                            <div>De: {new Date(coupon.validFrom).toLocaleDateString('pt-BR')}</div>
                          )}
                          {coupon.validUntil && (
                            <div>Até: {new Date(coupon.validUntil).toLocaleDateString('pt-BR')}</div>
                          )}
                        </div>
                      </div>
                    ) : (
                      'Sem limite'
                    )}
                  </td>
                  <td>
                    <span className={`badge ${getStatusBadge(coupon.status)}`}>
                      {getStatusText(coupon.status)}
                    </span>
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      <button
                        onClick={() => handleEdit(coupon)}
                        className="btn btn-secondary"
                        style={{ padding: '6px 8px' }}
                        title="Editar"
                      >
                        <Edit2 size={14} />
                      </button>
                      
                      {coupon.status === 'active' && (
                        <button
                          onClick={() => handleDeactivate(coupon.id)}
                          className="btn btn-secondary"
                          style={{ padding: '6px 8px' }}
                          title="Desativar"
                        >
                          <PowerOff size={14} />
                        </button>
                      )}
                      
                      <button
                        onClick={() => handleDelete(coupon.id)}
                        className="btn btn-danger"
                        style={{ padding: '6px 8px' }}
                        title="Excluir"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Coupons;
